"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.publicLinkResponseSchema = exports.fileAttachmentListSchema = exports.fileAttachmentSchema = exports.FILE_TARGETS = exports.SCAN_STATES = exports.RESCAN_AFTER_MS = exports.AVATAR_MAX_BYTES = exports.AVATAR_TYPES = exports.AGENT_IMAGE_EXTENSIONS = exports.AGENT_IMAGE_TYPES = exports.TABLE_EXTENSIONS = exports.INLINE_IMAGE_TYPES = exports.FILE_MAX_BYTES = void 0;
exports.isInlineImage = isInlineImage;
exports.agentImageTypeOf = agentImageTypeOf;
exports.tableFormatOf = tableFormatOf;
exports.blobPath = blobPath;
exports.blobDomainPath = blobDomainPath;
exports.needsRescan = needsRescan;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C22 · uploaded files. `decisions.md` R-20.                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The bytes live on a named volume behind the API, at a path derived from their own content hash.
 * This module holds everything about that which both sides need to agree on: the ceiling, what may
 * be shown inline, and how a hash becomes a path.
 */
/**
 * ⚠️ **15 MB, and it is checked BEFORE the bytes are read.** A limit enforced once the file is in
 * memory is not a limit — it is a description of what already happened. The multipart layer is
 * given this number so the connection is cut at the ceiling rather than after it.
 */
exports.FILE_MAX_BYTES = 15 * 1024 * 1024;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT MAY BE SHOWN INLINE. A CLOSED LIST, AND SVG IS NOT ON IT.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Anything served inline is served **from our own origin**, so it runs with our cookies and our
 * CSP. That is fine for a raster image and catastrophic for anything with a parser behind it.
 *
 * ⚠️ **SVG is download-only** (product owner, 28 Aug). It is an image *and* a script vector, and no
 * MIME type separates the two — an `<svg>` may carry `<script>`, an `onload`, or a foreign object.
 * Putting it here because it is technically an image is how an allow-list becomes a list of things
 * that look safe.
 */
exports.INLINE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
/** Whether the browser may render this in place, or must be handed it as a download. */
function isInlineImage(mimeType) {
    return exports.INLINE_IMAGE_TYPES.includes(mimeType);
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT MAY BE READ AS A TABLE. A closed list, by EXTENSION, and it     │
 * │ lives here because THREE places need the same answer.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `files/tabular.ts` decided this server-side from C43: the Agent's attachments must be tabular,
 * because a tool reads their rows. C48-1 gave the browser two reasons to know the same list — the
 * file picker's `accept` attribute, and a drag-and-drop, where `accept` does nothing at all — and a
 * second copy of four extensions is a second answer to *"what can I send"*. So: one list, one
 * predicate, and the server's refusal and the browser's are about the same set by construction.
 *
 * ⚠️⚠️ **THE EXTENSION, NOT THE MIME TYPE, AND THAT IS NOT LAZINESS.** A CSV has no magic number —
 * it is a text file, and no header distinguishes one from a shopping list — so a sniffer answers
 * `application/octet-stream`, which is honest and useless for this question. A browser is worse
 * again: `File.type` for a `.csv` is `text/csv` on one machine, `application/vnd.ms-excel` on a
 * machine with Excel installed, and `''` on a third.
 *
 * ⚠️ **This is NOT the security boundary and must not be read as one.** ClamAV scans every byte
 * before a link to it exists (R-18), the bytes are stored under a content hash and never a name
 * (`blobPath`), and nothing anywhere executes them. What this list buys is a *useful* refusal:
 * telling somebody their `.pdf` is not a spreadsheet, in words.
 */
exports.TABLE_EXTENSIONS = ['.csv', '.tsv', '.txt', '.xlsx'];
/**
 * ┌────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT THE AGENT MAY BE SHOWN — feedback round 4 item 14.                   │
 * └────────────────────────────────────────────────────────────────────┘
 *
 * C43 admitted tables only, because the one thing the agent could do with a file was read its rows.
 * A picture is the other thing a person has to hand — a screenshot of the screen they are asking
 * about — and the model can look at one, so refusing it was a limit of the plumbing rather than of
 * the model.
 *
 * ⚠️⚠️ **AN IMAGE IS NOT READ WITH A TOOL; IT IS PART OF THE MESSAGE.** A table is announced by
 * id and fetched by `files_read` when the model decides it needs the rows. An image has to be IN
 * the turn, as a content block, or the model never sees a pixel — `agent.service.ts` is where that
 * happens, and it is why this list is separate rather than four more entries in `TABLE_EXTENSIONS`.
 *
 * ⚠️ **MIME types here, unlike the tables' extensions, and for the opposite reason.** A PNG and a
 * JPEG both start with a magic number that says exactly what they are, so the sniffed type IS the
 * reliable answer — and the wire format the model wants is a media type, not a filename.
 *
 * ⚠️ **No GIF and no SVG.** An animation is many frames and the model is shown one; an SVG is a
 * document that can carry script, and `AVATAR_TYPES` excludes it for the same reason.
 */
exports.AGENT_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
/** The extensions those types arrive under — for a file picker's `accept`, and for a sentence. */
exports.AGENT_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];
/**
 * Whether the agent may be shown this file as a picture.
 *
 * ⚠️ **By sniffed MIME type where there is one, and by extension otherwise.** The server has
 * sniffed the bytes by the time it asks; the browser has only a name.
 */
function agentImageTypeOf(mimeType, filename) {
    const lower = mimeType.toLowerCase();
    /* ⚠️ A NARROW return type, not `string`: what this answers is sent as a media type on the wire,
       and a media type the model does not know is a refused request rather than a bad picture. */
    const known = exports.AGENT_IMAGE_TYPES.find((type) => type === lower);
    if (known !== undefined)
        return known;
    const name = filename.toLowerCase();
    if (name.endsWith('.png'))
        return 'image/png';
    if (name.endsWith('.jpg') || name.endsWith('.jpeg'))
        return 'image/jpeg';
    if (name.endsWith('.webp'))
        return 'image/webp';
    return null;
}
/**
 * Which of the two readers a filename is for, or `null` if neither.
 *
 * ⚠️ **`.txt` and `.tsv` are CSV**, because `readTable` sniffs the delimiter rather than trusting the
 * extension — a tab-separated export named `.txt` is the commonest thing a person actually has.
 */
function tableFormatOf(filename) {
    const name = filename.toLowerCase();
    if (name.endsWith('.xlsx'))
        return 'xlsx';
    return exports.TABLE_EXTENSIONS.some((extension) => extension !== '.xlsx' && name.endsWith(extension))
        ? 'csv'
        : null;
}
/** What an avatar may be. A narrower set than an attachment: no animation, no transparency games. */
exports.AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/**
 * ⚠️ **Avatars get their own, much smaller ceiling.** A 15 MB profile picture is a 15 MB file
 * fetched by every screen that draws a row, and nothing about a 64px circle needs it.
 */
exports.AVATAR_MAX_BYTES = 2 * 1024 * 1024;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE PATH. Derived from the hash, never from anything a caller sent.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `ab/cd/ef/<rest-of-hash>` — the first six hex characters as three two-character directories, the
 * remaining fifty-eight as the filename. Three levels because one flat directory of a hundred
 * thousand files is a directory listing nobody can use and a filesystem some people cannot tune;
 * 256 × 256 × 256 buckets is more than this will ever need and costs nothing empty.
 *
 * ⚠️⚠️ **THE UPLOADED FILENAME NEVER APPEARS HERE.** `imports/contract.ts` named this moment three
 * chunks early — *"`../../etc/passwd` is a harmless string here and would be a hole the moment
 * anything wrote a file"* — and deriving the path from the content closes it **structurally**,
 * which is the only version of that defence that cannot be argued back open. The original name is
 * a column, echoed back and used as a download name, never opened.
 *
 * ⚠️ **The hash is validated here rather than trusted**, even though every caller computes it
 * moments earlier. A `..` reaching this function is the whole vulnerability, so the check lives
 * where the path is built and not at the call sites that could forget it.
 */
function blobPath(contentHash) {
    if (!/^[0-9a-f]{64}$/.test(contentHash)) {
        throw new Error(`not a sha-256 hex digest: ${contentHash}`);
    }
    return `${contentHash.slice(0, 2)}/${contentHash.slice(2, 4)}/${contentHash.slice(4, 6)}/${contentHash.slice(6)}`;
}
/**
 * The directory a domain's blobs live under, relative to the store root.
 *
 * ⚠️⚠️ **THE TENANT SEGMENT IS THE ISOLATION ARGUMENT** (R-20). Content addressing alone would let
 * two tenants share one object: a little disk saved, in exchange for an existence oracle — upload a
 * file, observe whether it deduplicated, learn that another customer holds it — deletion coupled
 * between customers, and one corrupt object in front of several of them.
 *
 * ⚠️ **An avatar cannot live under a tenant.** `users.avatar_url` is on the identity table, which
 * spans tenants; one person can be in several. So there are two domains and they are exclusive,
 * which `file_blobs_domain` enforces in the database.
 */
function blobDomainPath(domain) {
    return 'tenantId' in domain ? `tenant/${domain.tenantId}` : `account/${domain.userId}`;
}
/**
 * ⚠️ **Re-scan after a month** (R-20). A hash already `clean` is not rescanned — the real payoff of
 * content addressing, and it means a popular attachment is scanned once rather than once per
 * sender. But signature databases update, so a verdict has a shelf life: a blob clean in January is
 * not clean in June.
 */
exports.RESCAN_AFTER_MS = 30 * 24 * 60 * 60 * 1000;
function needsRescan(scannedAt, now = new Date()) {
    if (scannedAt === null)
        return true;
    return now.getTime() - scannedAt.getTime() >= exports.RESCAN_AFTER_MS;
}
exports.SCAN_STATES = ['pending', 'clean', 'infected'];
/** What a link can hang on. Two today; tasks, projects and clients are a row each. */
/**
 * What a file can be attached to.
 *
 * ⚠️ **Each target answers to its OWN permission**, and `file.service.ts` holds the map: a comment
 * attachment is governed by `task.update` because that is what governs the comment, and a contract's
 * by `contract.manage` for the same reason. An attachment must not outlive the authority over its
 * subject — a single `file.manage` action would be exactly that.
 */
/**
 * ⚠️⚠️ **THIS LIST EXISTS IN POSTGRES TOO (`public.file_target`), AND ADDING TO ONE HALF COMPILES.**
 * `hard-won.md` C31-2 names seven such pairs and this is the eighth: a value added here and not
 * there is a `22P02` at runtime; added there and not here is a target no endpoint will accept.
 * `0061` is `workspace_icon`'s other half.
 */
exports.FILE_TARGETS = [
    'task_comment',
    /**
     * ⚠️⚠️ **THE TICKET'S OWN FOLDER, WHICH IS NOT `task_comment`.** A file on a comment belongs to
     * that comment and goes with it; a file on the TASK is the ticket's, and outlives every
     * conversation about it. The mockup draws both and the app had only the first, so the only way
     * to attach a specification was to comment with it — which buries it in a thread. `0115`.
     */
    'task',
    /**
     * ⚠️⚠️ **THE DESCRIPTION'S OWN, WHICH THE TASK'S IS NOT.** A file dropped into the description is
     * part of what the ticket SAYS; a file on the task is a document filed against it. The first
     * build put one file in both places and the product owner ruled it out: *"it shouldn't be
     * duplicated into the attachments, it lives on its own until it's deleted"*. Same distinction
     * `task_comment` draws, one level further in. `0117`.
     */
    'task_description',
    'user_avatar',
    'contract',
    /**
     * ⚠️ **The LINE ITEM's own, which a contract's is not.** 0044 stopped at the contract, so an
     * estimate or a client's sign-off for one line item landed in a list shared with the other
     * three. `0116`.
     */
    'line_item',
    /**
     * ⚠️⚠️ **A PERSON'S FOLDER, WHICH IS NOT `user_avatar`.** The avatar is the member's own — no
     * `workspace_id`, governed by nobody else, shown in every workspace they share. This is the
     * workspace's record ABOUT them: a contract, an NDA, a certificate, governed by whoever
     * administers people. One target could not hold both authorities. `0116`.
     */
    'person',
    'workspace_icon',
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ C43 · A FILE SOMEBODY HANDED THE AGENT. `0086` is the other half. │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * *"Itt van a hónapom, ezeket írd be"* — a CSV or a spreadsheet attached to one conversation, read
     * by the `files_read` tool and turned into a batch of worklogs somebody confirms on one card.
     *
     * ⚠️⚠️ **IT ANSWERS TO NO WORKSPACE PERMISSION, AND THAT IS THE ONE THING UNUSUAL ABOUT IT.**
     * Every other target inherits the authority over its subject — a comment's attachment is governed
     * by `task.update` because that is what governs the comment. A conversation belongs to **one
     * member** (`0042`), so what governs its files is the same thing that governs the transcript:
     * being the member whose conversation it is. `file.service.ts` checks that, and `0087` makes the
     * database agree, because the ordinary `file_links` policy is workspace-wide and would let a
     * colleague read the row.
     *
     * ⚠️ **The tenant's agent flag still applies** (`TARGET_FLAGS`), so a tenant without the Agent
     * cannot attach files to a feature they do not have.
     */
    'agent_conversation',
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️ P3-9 · the medical certificate. `0113` is the other half.          │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * A leave type may set `attachment_rule = 'required'`, which C46 shipped and nothing enforced
     * because there was nowhere to put a file. Approval now refuses without one.
     *
     * ⚠️ **Gated by OWNERSHIP OR `leave.decide`, like the conversation above and unlike everything
     * else here.** A requester has no `leave.*` action at all and must still be able to attach their
     * own certificate; a decider must be able to read it. Both halves are in `file.service.ts`.
     */
    'leave_request',
    /**
     * ┌───────────────────────────────────────────────────────────────┐
     * │ ⚠️ C56 · the supplier's invoice. `0133` is the other half.               │
     * └───────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **A COST ROW IS A CLAIM; THE PDF BESIDE IT IS THE EVIDENCE.** *1 250 000 Ft, Relevance
     * Kft.* is what somebody typed — the invoice is what it is based on, and the one thing a finance
     * reader goes looking for when a figure surprises them.
     *
     * ⚠️ Its authority is its CONTRACT's, exactly like a line item's above.
     */
    'external_cost',
    /**
     * ┌───────────────────────────────────────────────────────────────┐
     * │ ⚠️ C57 · the issued invoice itself. `0134` is the other half.  │
     * └───────────────────────────────────────────────────────────────┘
     *
     * The PDF the accounting system produced, filed against the row that records it. The detail
     * screen's own words: *"Húzd be a kiállított számla PDF-jét."*
     *
     * ⚠️ Its authority is its CONTRACT's, exactly like a line item's and a cost's above.
     */
    'invoice',
    /* 20 Oct 2026 · Phase 6 · a deal's files (`0228`). */
    'deal',
];
/**
 * One attachment, as a screen sees it.
 *
 * ⚠️ **There is no URL in here.** The client builds one from `id` against its own API base, because
 * a URL minted on the server would be a second place that knows how files are addressed — and the
 * one that goes stale when the route changes. `downloadName` is what the browser should save it as.
 */
exports.fileAttachmentSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    /**
     * `file_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`.
     *
     * ⚠️ **It names the STORED FILE (`file_blobs`), not this link.** `id` is the link's — the same
     * bytes attached twice are two links carrying one `identifier`, which is the one to quote.
     */
    identifier: (0, identifier_1.identifierSchema)('file'),
    filename: zod_1.z.string(),
    byteSize: zod_1.z.number().int().positive(),
    mimeType: zod_1.z.string(),
    /** ⚠️ Derived from `mimeType` against the closed list — the client never decides this itself. */
    inline: zod_1.z.boolean(),
    uploadedAt: zod_1.z.string(),
    uploadedByName: zod_1.z.string().nullable(),
});
exports.fileAttachmentListSchema = zod_1.z.object({ data: zod_1.z.array(exports.fileAttachmentSchema) });
/**
 * `POST /files/:id/public-link` — a URL that shows this image to anybody who has it (`0180`).
 *
 * ⚠️⚠️ **THERE IS A URL HERE, UNLIKE `fileAttachmentSchema`, AND THAT IS THE POINT.** A public link
 * is minted by the server and exists to leave it — into an email, which cannot ask our API base for
 * anything. Images only; anything else is `unsupported_file_type`.
 */
exports.publicLinkResponseSchema = zod_1.z.object({ url: zod_1.z.string() });
//# sourceMappingURL=contract.js.map